import { ApplicantTabNavigation } from "@components/layout/ApplicantTabNavigation";
import { ApplicationHeader } from "@components/layout/ApplicationHeader";
import { getApplicationHeaderProps } from "@lib/utils/applicationHeaderHelpers";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import {
  useState,
  useEffect,
  type MouseEvent,
} from "react";
import {
  PenTool,
  ArrowRight,
  X,
  Save,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Loader2,
  FileText,
  FileSignature,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectApplicationData,
  selectApplicationLoading,
  selectIsReadOnly,
  fetchApplicationById,
  fetchApplications,
  clearError,
} from "@/features/applicant/applicationSlice";
import { SSNInput } from "@components/ui/SSNInput";
import { updateApplication } from "@/features/applicant/applicationSlice";
import apiClient from "@/lib/api/apiClient";
import {
  fetchCreditBureauActive,
  fetchCreditBureauDetails,
  type CreditBureauConfig,
} from "@/lib/api/applicationService";
import {
  residentTabValidation,
  type UpdateApplicationRequest,
} from "@/types/application";
import { useNavigate, useParams } from "react-router-dom";
import {
  getTabPathWithApplicantId,
  getTabNavigationOptions,
} from "@lib/utils/applicationNavigation";
import { useTabValidation, useTabAutoSave } from "@/features/applicant/context/TabValidationContext";
import { ValidationModal } from "@components/ui/ValidationModal";
import { applicantValidationModalProps } from "@/features/applicant/constants/applicantDialogProps";
import { SignatureDisclosureModal } from "@/features/applicant/components/SignatureDisclosureModal";
import type { DisclosureLinkId } from "@/features/applicant/types/pages";
import {
  trackApplicationAutosave,
  trackApplicationSectionComplete,
} from "@/lib/utils/analytics";

export function Signature() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();
  const applicationData = useAppSelector(selectApplicationData);
  const isLoading = useAppSelector(selectApplicationLoading);
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const { registerValidator, unregisterValidator } = useTabValidation();

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;
  const [hasFetched, setHasFetched] = useState(false);
  const [link1Checked, setLink1Checked] = useState(false);
  const [link2Checked, setLink2Checked] = useState(false);
  const [link3Checked, setLink3Checked] = useState(false);
  const [ssnDigits, setSsnDigits] = useState<string>("");
  const [signature, setSignature] = useState<string>("");

  const [signatureError, setSignatureError] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingExit, setIsSavingExit] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalMessage, setValidationModalMessage] = useState("");
  const [validationShowIcon, setValidationShowIcon] = useState(true);
  // Checkbox matching Angular: access_statement (uc_optin state kept for API payload)
  const [ucOptIn, setUcOptIn] = useState(false);
  const [accessStatementChecked, setAccessStatementChecked] = useState(false);
  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<{
    allDocumentsChecked?: string;
    signature?: string;
    accessStatement?: string;
    ssn?: string;
  }>({});
  const [validateForm, setValidateForm] = useState(false);

  const [creditBureauConfig, setCreditBureauConfig] = useState<CreditBureauConfig | null>(null);
  const isImpersonate = !!localStorage.getItem("admin_token") || localStorage.getItem("impersonate") === "true";

  useEffect(() => {
    async function loadCreditBureauConfig() {
      try {
        const cbActive = await fetchCreditBureauActive();
        let config = cbActive;
        if (applicantId) {
          const cbDetails = await fetchCreditBureauDetails(applicantId);
          if (cbDetails?.active) config = cbDetails;
        }
        setCreditBureauConfig(config);
      } catch {
        /* keep null — falls back to hardcoded Equifax defaults */
      }
    }
    loadCreditBureauConfig();
  }, [applicantId]);

  const bureauName = creditBureauConfig?.details?.config_value || "Equifax Information Services, LLC";
  const bureauAddress = creditBureauConfig?.details
    ? `${creditBureauConfig.details.cb_addressline1 || ""}, ${creditBureauConfig.details.cb_city || ""}, ${creditBureauConfig.details.cb_state || ""} ${creditBureauConfig.details.cb_zipcode || ""}`
    : "P.O. Box 740256, Atlanta, GA 30374";
  const bureauPhone = creditBureauConfig?.details?.cb_telephone || "(866) 349-5191";
  const bureauFax = creditBureauConfig?.details?.cb_fax || "";
  const isEquifax = creditBureauConfig?.active === "Equifax" || !creditBureauConfig;
  const bureauWeb = isEquifax ? "https://www.equifax.com/personal/" : "";

  // Disclosure modal: show document in popup, scroll to acknowledge, then Accept checks outer checkbox
  const [disclosureModalLinkId, setDisclosureModalLinkId] = useState<
    DisclosureLinkId | null
  >(null);

  // Check if all required documents are checked (terms reading)
  const allDocumentsChecked = link1Checked && link2Checked && link3Checked;

  const openDisclosureModal = (linkId: DisclosureLinkId) => {
    setDisclosureModalLinkId(linkId);
  };

  const handleDisclosureLinkClick = (
    event: MouseEvent<HTMLButtonElement>,
    linkId: DisclosureLinkId,
  ) => {
    event.preventDefault();
    openDisclosureModal(linkId);
  };

  const closeDisclosureModal = () => {
    setDisclosureModalLinkId(null);
  };

  const handleDisclosureAccept = (linkId: DisclosureLinkId) => {
    if (linkId === 1) {
      setLink1Checked(true);
    } else if (linkId === 2) {
      setLink2Checked(true);
    } else {
      setLink3Checked(true);
    }
    if (fieldErrors.allDocumentsChecked) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.allDocumentsChecked;
        return next;
      });
    }
    closeDisclosureModal();
  };

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Function to handle signature name mismatch error (called when API returns error)
  const handleSignatureNameMismatch = async () => {
    setValidationModalMessage(APPLICANT_CONTENT.errors.signature.signatureMismatch);
    setValidationShowIcon(false);
    setShowValidationModal(true);

    // Prevent API calls when application is read-only
    if (isReadOnly) {
      return;
    }

    // Log the name override request to backend (for tracking)
    if (applicationData?.applicant_id) {
      try {
        await apiClient.post(
          `/api/name_override/${applicationData.applicant_id}`,
        );
        // No need to handle response - this is just for logging
      } catch {
        // Silently fail - this is just for tracking, don't show error to user
      }
    }
  };

  // Get expected signature from application data
  const getExpectedSignature = (): string => {
    if (!applicationData) return "";
    const firstName = applicationData.first_name || "";
    const lastName = applicationData.last_name || "";
    return `${firstName} ${lastName}`.trim();
  };

  // Validate signature matches first and last name according to business logic
  // Signature must match first name + last name (case-insensitive)
  const validateSignature = (value: string): boolean => {
    if (!applicationData || !value.trim()) return true; // If no application data or empty, allow

    const firstName = applicationData.first_name || "";
    const lastName = applicationData.last_name || "";

    if (!firstName || !lastName) return true; // If missing name data, allow

    const fullName = `${firstName} ${lastName}`.toLowerCase().trim();
    const signature = value.toLowerCase().trim();

    return signature === fullName;
  };

  // Listen for messages from notice pages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) {
        return;
      }

      const { type, linkType } = event.data;

      if (type === "linkOpened" || type === "linkClosed") {
        // When a link is opened or closed, check the corresponding checkbox
        if (linkType === "terms_fair") {
          setLink1Checked(true);
        } else if (linkType === "terms_credit") {
          setLink2Checked(true);
        } else if (linkType === "authorization_for_liberty") {
          setLink3Checked(true);
        }

        // Clear validation error - will be checked after state updates
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.allDocumentsChecked;
          return newErrors;
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Fetch application data on mount if not already loaded
  useEffect(() => {
    if (!applicationData?.applicant_id && !isLoading && !hasFetched) {
      setHasFetched(true);
      // Try to get applicant_id from storage
      const storedApplicantId = localStorage.getItem("applicant_id");
      if (storedApplicantId) {
        dispatch(fetchApplicationById(storedApplicantId));
      } else {
        dispatch(fetchApplications());
      }
    }
  }, [applicationData?.applicant_id, isLoading, hasFetched, dispatch]);

  // Note: fetchApplicationById is called by the parent ResidentApplication component,
  // which waits for it to complete before rendering this tab. No need to fetch here.

  // Load and autofill all existing data from application data (like About page does)
  useEffect(() => {
    if (applicationData) {
      const appData = applicationData;

      // Load SSN from application data - store as raw digits for SSNInput component
      // Check both security_number at root level (like in the API response)
      if (appData.security_number) {
        // Extract digits - handle both string and number formats
        const ssnString = String(appData.security_number);
        const digits = ssnString.replace(/\D/g, "");
        if (digits.length >= 9) {
          // Take first 9 digits if more than 9
          setSsnDigits(digits.slice(0, 9));
        } else if (digits.length > 0) {
          // If less than 9 digits, still set what we have
          setSsnDigits(digits);
        }
      }

      // Load signature from digital_signature (JSON string format)
      if (appData.digital_signature) {
        try {
          // digital_signature is stored as JSON string: "{\"signature\":\"Web Check\",\"signaturetype\":\"type1\"}"
          const signatureObj =
            typeof appData.digital_signature === "string"
              ? JSON.parse(appData.digital_signature)
              : appData.digital_signature;

          if (signatureObj && signatureObj.signature) {
            setSignature(signatureObj.signature);
          }
        } catch {
          // If parsing fails, try direct signature field
          if (appData.signature) {
            setSignature(appData.signature);
          }
        }
      } else if (appData.signature) {
        // Fallback to direct signature field
        setSignature(appData.signature);
      }

      // Load all_links_read - check all three links if it's set
      if (
        appData.all_links_read === "1" ||
        appData.all_links_read === 1 ||
        appData.all_links_read === true
      ) {
        setLink1Checked(true);
        setLink2Checked(true);
        setLink3Checked(true);
      }

      // Load uc_optin (Utility Concierge opt-in) from application data
      if (appData.uc_optin === "1" || appData.uc_optin === 1) {
        setUcOptIn(true);
      }

      // Load access_statement from application data
      if (appData.access_statement === "1" || appData.access_statement === 1) {
        setAccessStatementChecked(true);
      }

    }
  }, [applicationData]);  

  // Register validator for Tab Validation
  useEffect(() => {
    const validateSignatureTab = (): { isValid: boolean; message?: string } => {
      const errorMessages: string[] = [];

      // 1. Terms and Documents
      if (!link1Checked || !link2Checked || !link3Checked) {
        errorMessages.push(
          "Terms and Documents: Please read and agree to all terms and documents.",
        );
      }

      // 2. SSN
      if (!ssnDigits || ssnDigits.length !== 9) {
        errorMessages.push("SSN: Please provide a valid 9-digit SSN.");
      }

      // 3. Access Statement
      if (!accessStatementChecked) {
        errorMessages.push("Statements: Please agree to the access statement.");
      }

      // 4. Signature
      if (!signature.trim()) {
        errorMessages.push("Signature: Signature is required.");
      } else if (!validateSignature(signature)) {
        const expected = getExpectedSignature();
        errorMessages.push(`Signature: Signature must match "${expected}"`);
      }

      if (errorMessages.length > 0) {
        return {
          isValid: false,
          message: errorMessages.join("\n"),
        };
      }

      return { isValid: true };
    };

    registerValidator("signature", validateSignatureTab);
    return () => unregisterValidator("signature");
  }, [
    registerValidator,
    unregisterValidator,
    link1Checked,
    link2Checked,
    link3Checked,
    applicationData,
    ssnDigits,
    accessStatementChecked,
    signature,
    applicationData?.first_name,
    applicationData?.last_name,
  ]);

  // Handle save and continue - Validate ALL fields before making ANY API calls (exitAfterSave = true for Save & Exit)
  const handleSaveAndContinue = async (exitAfterSave = false) => {
    const navigateToDashboardAfterSave = exitAfterSave;
    // If read-only mode, just navigate to next section without saving
    if (isReadOnly) {
      const paymentPath = applicantId
        ? getTabPathWithApplicantId("payment", applicantId)
        : "/applicant/application/payment";
      if (applicantId) {
        navigate(paymentPath, getTabNavigationOptions("payment"));
      } else {
        navigate(paymentPath);
      }
      return;
    }

    // Clear any previous messages
    setSaveMessage(null);

    // Validation 1: Application ID check (no API call needed)
    if (!applicationData?.applicant_id) {
      setSaveMessage({
        type: "error",
        text: "Application ID not found. Please refresh the page.",
      });
      setTimeout(() => setSaveMessage(null), 5000);
      return; // Stop - no API calls
    }

    // Validate all fields
    const errors: typeof fieldErrors = {};

    // Validation 2: All terms must be checked (no API call needed)
    if (!allDocumentsChecked) {
      errors.allDocumentsChecked = APPLICANT_CONTENT.errors.signature.allDocumentsChecked;
    }

    // Validation 3: Signature required (no API call needed)
    if (!signature.trim()) {
      errors.signature = APPLICANT_CONTENT.errors.signature.signature;
    } else if (!validateSignature(signature)) {
      setValidationModalMessage(APPLICANT_CONTENT.errors.signature.signatureMismatch);
      setValidationShowIcon(false);
      setShowValidationModal(true);
      const firstName = applicationData?.first_name || "";
      const lastName = applicationData?.last_name || "";
      errors.signature = `Signature must match "${firstName} ${lastName}"`;
    }

    // Validation 4: Access statement required (no API call needed)
    if (!accessStatementChecked) {
      errors.accessStatement = "'Statements' is required.";
    }

    // Validation 5: SSN validation (no API call needed)
    if (!ssnDigits || ssnDigits.length !== 9) {
      errors.ssn = APPLICANT_CONTENT.errors.signature.ssnDigits;
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setValidateForm(true);
      // Show validation popup with all errors
      const errorMessages: string[] = [];
      if (errors.allDocumentsChecked)
        errorMessages.push(
          `Terms and Documents: ${errors.allDocumentsChecked}`,
        );
      if (errors.signature)
        errorMessages.push(`Signature: ${errors.signature}`);
      if (errors.accessStatement)
        errorMessages.push(`Agreement: ${errors.accessStatement}`);
      if (errors.ssn) errorMessages.push(`SSN: ${errors.ssn}`);
      if (errorMessages.length > 0) {
        setValidationModalMessage(errorMessages.join("\n"));
        setValidationShowIcon(true);
        setShowValidationModal(true);
      }
      return;
    }

    // Clear errors if validation passes
    setFieldErrors({});
    setValidateForm(false);
    setSignatureError("");

    // ALL VALIDATIONS PASSED - Now proceed with API calls

    if (navigateToDashboardAfterSave) {
      setIsSavingExit(true);
    } else {
      setIsSaving(true);
    }
    setSaveMessage(null);

    try {
      const updateData = buildSignatureUpdateData();

      await dispatch(
        updateApplication({
          applicantId: applicationData.applicant_id,
          updateData: updateData,
        }),
      ).unwrap();

      // Fetch updated data
      await dispatch(
        fetchApplicationById(applicationData.applicant_id),
      ).unwrap();

      setSaveMessage({
        type: "success",
        text: "Signature saved successfully!",
      });
      trackApplicationAutosave("acknowledgements_identity", 5, "success");
      if (!navigateToDashboardAfterSave) {
        trackApplicationSectionComplete("acknowledgements_identity", 5);
      }

      // Navigate to dashboard (Save & Exit) or payment page (Save & Continue)
      setTimeout(() => {
        if (navigateToDashboardAfterSave) {
          navigate("/applicant/dashboard", { replace: true });
        } else {
          const paymentPath = applicantId
            ? getTabPathWithApplicantId("payment", applicantId)
            : "/applicant/application/payment";
          if (applicantId) {
            navigate(paymentPath, {
              ...getTabNavigationOptions("payment"),
              replace: true,
            });
          } else {
            navigate(paymentPath, { replace: true });
          }
        }
      }, 300);
    } catch (error) {
      trackApplicationAutosave("acknowledgements_identity", 5, "error");
      // Clear Redux error state immediately to prevent parent ResidentApplication
      // component's useEffect from redirecting to /applicant/dashboard.
      // The parent watches selectApplicationError and navigates away on any error,
      // but here we want to handle the error locally (show popup/message).
      dispatch(clearError());

      // Check if error is related to signature name mismatch
      let errorMessage =
        typeof error === "string"
          ? error
          : "Failed to save signature. Please try again.";

      // Try to extract error message from API response (prefer server body over axios generic message)
      if (error && typeof error === "object") {
        const apiError = error as {
          response?: {
            data?:
              | string
              | { message?: string; error?: string | { message?: string } };
          };
          message?: string;
          error?: string | { message?: string };
        };

        if (apiError.response?.data) {
          const responseData = apiError.response.data;
          if (typeof responseData === "string") {
            errorMessage = responseData;
          } else if (typeof responseData === "object" && responseData.message) {
            errorMessage = responseData.message;
          } else if (typeof responseData === "object" && responseData.error) {
            errorMessage =
              typeof responseData.error === "string"
                ? responseData.error
                : responseData.error?.message ?? errorMessage;
          }
        } else if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.error) {
          if (typeof apiError.error === "string") {
            errorMessage = apiError.error;
          } else if (
            typeof apiError.error === "object" &&
            apiError.error.message
          ) {
            errorMessage = apiError.error.message;
          }
        }
      }

      // Check if error message contains signature validation error
      const errorString = String(errorMessage);
      if (
        errorString.includes(
          APPLICANT_CONTENT.errors.signature.signatureMismatch.slice(0, 57),
        )
      ) {
        // Handle signature name mismatch - show popup and log the event
        await handleSignatureNameMismatch();
        setIsSaving(false);
        setIsSavingExit(false);
        return; // Don't show error in saveMessage, popup is already shown
      }

      // Same UX as client-side tab validation: modal with server message (e.g. SSN / ITIN required)
      setValidationModalMessage(errorMessage);
      setValidationShowIcon(true);
      setShowValidationModal(true);
    } finally {
      setIsSaving(false);
      setIsSavingExit(false);
    }
  };

  // Build the signature tab API payload from current form state.
  // Single source of truth for both the Save button and auto-save-on-tab-change.
  // Identity verification (DL/Passport or Verifast) is handled on the About tab.
  const buildSignatureUpdateData = (): UpdateApplicationRequest => {
    const signatureData = {
      signature: signature.trim(),
      signaturetype: "type1",
    };

    const updateData: UpdateApplicationRequest = {
      ...residentTabValidation("signature"),
      digital_signature: JSON.stringify(signatureData), // JSON string format
      access_statement: accessStatementChecked ? "1" : "0", // From checkbox
      uc_optin: ucOptIn ? "1" : "0", // Utility Concierge opt-in from checkbox
      all_links_read: allDocumentsChecked ? "1" : "0", // String format
      enter_date: {}, // Required empty object (like History page)
    };

    // Remove undefined fields (like About page does)
    Object.keys(updateData).forEach((key) => {
      if (updateData[key as keyof typeof updateData] === undefined) {
        delete updateData[key as keyof typeof updateData];
      }
    });

    return updateData;
  };

  // Auto-save-on-tab-change (Angular submitForm('fromtab') parity): persist the
  // current tab WITHOUT navigating or showing dialogs. Dirty-guarded by useTabAutoSave.
  const autoSaveSignature = async (): Promise<void> => {
    if (!applicationData?.applicant_id) return;
    await dispatch(
      updateApplication({
        applicantId: applicationData.applicant_id,
        updateData: buildSignatureUpdateData(),
      }),
    ).unwrap();
    await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap();
  };

  useTabAutoSave({
    name: "signature",
    snapshot: buildSignatureUpdateData(),
    loadedKey: applicationData?.applicant_id,
    save: autoSaveSignature,
    isReadOnly,
  });

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(
          applicationData,
          APPLICANT_CONTENT.application.status.incomplete,
        )}
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div data-testid="signature-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <PenTool className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {APPLICANT_CONTENT.application.steps.signature.name}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {APPLICANT_CONTENT.application.steps.signature.subtitle}
              </p>
            </div>
          </div>


          {/* Disclosures, Certifications, SSN & Signature */}
          <div className="mb-8 p-6 rounded-lg border-2 border-gray-300">

            {/* Impersonation warning - matches Angular */}
            {isImpersonate && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-sm font-semibold text-amber-800">
                Warning: All the terms and signature related information must be filled by applicant itself.
              </div>
            )}

            {/* Spanish FCRA notice - matches Angular summary-info */}
            <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-700">
              <p>Para informatión en español, visite{" "}
                <a href="https://www.consumerfinance.gov/learnmore" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                  www.consumerfinance.gov/learnmore
                </a>{" "}
                o escribe a la Consumer Financial Protection Bureau, 1700 G Street N.W., Washington, DC 20552.
              </p>
            </div>

            {/* Required Disclosures Section - subhead like Identity Verification */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-violet-600" />
                </span>
                Required Disclosures <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-red-600 font-semibold mb-3">
                You must click on all links in order to continue
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
                  <input
                    type="checkbox"
                    data-testid="signature-disclosure-1-checkbox"
                    checked={link1Checked}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        if (e.target.checked) {
                          openDisclosureModal(1);
                        } else {
                          setLink1Checked(false);
                        }
                      }
                    }}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    data-testid="signature-disclosure-1-view-btn"
                    onClick={(event) => handleDisclosureLinkClick(event, 1)}
                    className="text-left text-blue-600 hover:text-blue-800 underline text-sm flex-1 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    A Summary of Your Rights Under the Fair Credit Reporting Act
                  </button>
                </div>
                <div className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
                  <input
                    type="checkbox"
                    data-testid="signature-disclosure-2-checkbox"
                    checked={link2Checked}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        if (e.target.checked) {
                          openDisclosureModal(2);
                        } else {
                          setLink2Checked(false);
                        }
                      }
                    }}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    data-testid="signature-disclosure-2-view-btn"
                    onClick={(event) => handleDisclosureLinkClick(event, 2)}
                    className="text-left text-blue-600 hover:text-blue-800 underline text-sm flex-1 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Notice Regarding Background Investigation Pursuant to
                    California Law
                  </button>
                </div>
                <div className="flex items-start gap-3 p-2 hover:bg-white rounded transition-colors">
                  <input
                    type="checkbox"
                    data-testid="signature-disclosure-3-checkbox"
                    checked={link3Checked}
                    onChange={(e) => {
                      if (!isReadOnly) {
                        if (e.target.checked) {
                          openDisclosureModal(3);
                        } else {
                          setLink3Checked(false);
                        }
                      }
                    }}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    data-testid="signature-disclosure-3-view-btn"
                    onClick={(event) => handleDisclosureLinkClick(event, 3)}
                    className="text-left text-blue-600 hover:text-blue-800 underline text-sm flex-1 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Authorization for Liberty Rent to Collect Payments
                  </button>
                </div>
              </div>
              {validateForm && fieldErrors.allDocumentsChecked && (
                <div className="mt-2 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.allDocumentsChecked}
                </div>
              )}
            </div>

            {/* Acknowledgment and Authorization Section - subhead like Identity Verification */}
            <div className="mb-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 shadow-sm">
              <label className="block text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FileSignature className="w-4 h-4 text-violet-600" />
                </span>
                Acknowledgment and Authorization
              </label>
              <p className="text-xs text-red-600 mb-4 font-semibold bg-red-50 p-2 rounded border-l-4 border-red-400">
                [IMPORTANT -- PLEASE READ CAREFULLY BEFORE SIGNING ACKNOWLEDGMENT]
              </p>
              <div className="space-y-4 text-sm text-gray-700 leading-relaxed bg-white p-4 rounded border border-gray-200">
                <p>
                  I acknowledge receipt of the DISCLOSURE REGARDING BACKGROUND INVESTIGATION and A SUMMARY OF YOUR RIGHTS UNDER THE FAIR CREDIT REPORTING ACT and certify that I have read and understand both of those documents.
                </p>
                <p>
                  I hereby authorize the obtaining of consumer reports and/or
                  investigative consumer reports at any time after receipt of this
                  authorization and, if I am approved for residency, throughout my
                  tenancy.
                </p>
                <p>
                  To this end, I hereby authorize, without reservation, any law
                  enforcement agency, administrator, state or federal agency,
                  institution, school or university (public or private),
                  information service bureau, employer, or insurance company to
                  furnish any and all background information requested by{" "}
                  <strong>
                    {bureauName}, {bureauAddress}, Phone: {bureauPhone}
                    {bureauFax ? `, Fax: ${bureauFax}` : ""}
                    {bureauWeb && (
                      <>, Web:{" "}
                        <a
                          href={bureauWeb}
                          className="text-blue-600 hover:text-blue-800 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bureauWeb}
                        </a>
                      </>
                    )}
                  </strong>{" "}
                  {isEquifax && "(Look for the Free Credit Report, Lifting Freezes, Correcting or Disputing your credit information), "}
                  another outside organization
                  acting on behalf of the Company, and/or the Company itself.
                </p>
                <p>
                  I agree that an electronic or photographic copy of this
                  Authorization shall be as valid as the original.
                </p>
                <p>
                  I understand that by typing my name and the last four digits of
                  my Social Security Number or User ID, and clicking on the APPLICANT_CONTENT.application.buttons.next
                  button below, constitutes my electronic signature, dated as of
                  when I click on the "Sign Application" button, and that by doing
                  so:
                </p>
              </div>

            {/* Certifications */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-900 mb-3">
                Certifications:
              </p>
              <div className="space-y-2.5 text-sm text-gray-700">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I am authorizing {bureauName} to conduct
                    the background check(s) described above
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I am consenting to use electronic means to sign this form and
                    have read and understand the above disclosure
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify I have read and understand the Summary Of Your
                    Rights Under The Fair Credit Reporting Act
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify I have read and understand the Notice Regarding
                    Background Investigation Pursuant To California Law
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify that the information submitted is correct and true.
                    I understand that providing false or tampered with
                    documentation will result in a denial of my application.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I certify I have read, agree to the Authorization for Liberty
                    Rent to Collect Payments.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    The applicant expressly gives permission to applicant property
                    to share personal information with Liberty Rent through the
                    life of the lease contract.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I understand that my Liberty Rent Application Fee is
                    Non-Refundable, unless I am Denied by Liberty Rent.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I understand that the Liberty Rent Certificate Fee is
                    Non-Refundable.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="flex-1">
                    I acknowledge I may request a hard copy of this Disclosure and
                    Authorization form after agreeing to the background check
                    electronically by emailing Liberty Rent at Email:{" "}
                    <a
                      href="mailto:help@libertyrent.com"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      help@libertyrent.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Consent Text */}
            <div className="mb-6 space-y-4">
              <p className="text-sm text-gray-700">
                I hereby consent to receive disclosures and information
                electronically on this website and to electronically sign
                documents.
              </p>
              <p className="text-sm text-gray-700">
                If you do not wish to complete and sign this application
                electronically, please contact us for instructions
              </p>
            </div>

            {/* SSN or ITIN */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                SSN or ITIN <span className="text-red-500">*</span>
              </label>
              <SSNInput
                value={ssnDigits}
                onChange={(value) => {
                  setSsnDigits(value);
                  if (fieldErrors.ssn) {
                    setFieldErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.ssn;
                      return newErrors;
                    });
                  }
                }}
                disabled={true}
                placeholder="XXX-XX-XXXX"
                className={`w-full max-w-md px-3 py-2 border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent ${validateForm && fieldErrors.ssn
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300"
                  }`}
              />
              {validateForm && fieldErrors.ssn && (
                <div className="mt-1 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.ssn}
                </div>
              )}
            </div>

            {/* Access Statement - "I have read and agree with these statements" - just prior to signature */}
            <div className="mb-6 space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="access_statement"
                  checked={accessStatementChecked}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      setAccessStatementChecked(e.target.checked);
                      if (e.target.checked && fieldErrors.accessStatement) {
                        setFieldErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.accessStatement;
                          return newErrors;
                        });
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className={`mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 ${isReadOnly
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                    }`}
                />
                <label
                  htmlFor="access_statement"
                  className={`text-sm leading-relaxed ${isReadOnly
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 cursor-pointer"
                    }`}
                >
                  I have read and agree with these statements
                </label>
              </div>
              {validateForm && fieldErrors.accessStatement && (
                <div className="ml-7 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.accessStatement}
                </div>
              )}
            </div>

            {/* Signature */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Signature <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                By completing this field, the applicant consents to electronically
                signing this application with Liberty Rent
              </p>
              {!allDocumentsChecked && (
                <p className="text-xs text-amber-600 mb-2 font-medium">
                  Please check all required documents above to enable signature
                  field.
                </p>
              )}
              {allDocumentsChecked && !accessStatementChecked && (
                <p className="text-xs text-amber-600 mb-2 font-medium">
                  Please check &quot;I have read and agree with these statements&quot; above to enable signature field.
                </p>
              )}
              <input
                type="text"
                value={signature}
                onChange={(e) => {
                  const value = e.target.value;
                  setSignature(value);
                  // Clear errors when user starts typing (but keep modal open if it's showing)
                  if (fieldErrors.signature) {
                    setFieldErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.signature;
                      return newErrors;
                    });
                  }
                  if (signatureError) {
                    setSignatureError("");
                  }
                }}
                disabled={isReadOnly || !allDocumentsChecked || !accessStatementChecked}
                placeholder={
                  allDocumentsChecked && accessStatementChecked
                    ? `Enter: ${getExpectedSignature() || "First and Last Name"}`
                    : !allDocumentsChecked
                      ? "Check all required documents first"
                      : "Check \"I have read and agree with these statements\" first"
                }
                className={`w-full max-w-md px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${validateForm && fieldErrors.signature
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300"
                  } ${!allDocumentsChecked || !accessStatementChecked
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white"
                  }`}
              />
              {validateForm && fieldErrors.signature && (
                <div className="mt-1 text-sm text-red-600">
                  <AlertCircle className="inline-block w-4 h-4 mr-2" />
                  {fieldErrors.signature}
                </div>
              )}
            </div>

            {/* Agreement Detail - shown when access_statement is checked and signature_date exists */}
            {String(applicationData?.access_statement) === "1" &&
              applicationData?.signature_date && (
                <div className="mt-4 mb-6">
                  <p className="text-sm font-bold text-gray-900">
                    Agreement Detail: Agreed on{" "}
                    {new Date(
                      typeof applicationData.signature_date === "string" &&
                        !isNaN(Number(applicationData.signature_date))
                        ? Number(applicationData.signature_date) * 1000
                        : applicationData.signature_date,
                    )
                      .toLocaleString("en-US", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      })
                      .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")}{" "}
                    from {applicationData?.ip_address || "N/A"}
                  </p>
                </div>
              )}

            </div>

          </div>

          {/* Save Message - Only show success messages */}
          {saveMessage && saveMessage.type === "success" && (
            <div className="mb-4 p-4 rounded-lg flex items-start gap-3 bg-green-50 border border-green-200">
              <AlertTriangle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  {saveMessage.text}
                </p>
              </div>
              <button
                data-testid="signature-save-message-dismiss-button"
                onClick={() => setSaveMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <SignatureDisclosureModal
          linkId={disclosureModalLinkId}
          creditBureauConfig={creditBureauConfig}
          isReadOnly={isReadOnly}
          onClose={closeDisclosureModal}
          onAccept={handleDisclosureAccept}
        />

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 pb-6">
          <button
            data-testid="signature-back-button"
            type="button"
            onClick={() => {
              const historyPath = applicantId
                ? getTabPathWithApplicantId("history", applicantId)
                : "/applicant/application/history";
              if (applicantId) {
                navigate(historyPath, getTabNavigationOptions("history"));
              } else {
                navigate(historyPath);
              }
            }}
            disabled={isSaving || isSavingExit}
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {APPLICANT_CONTENT.application.buttons.back}
          </button>
          {!isReadOnly && (
            <button
              data-testid="signature-save-continue-button"
              onClick={() => handleSaveAndContinue()}
              disabled={
                isSaving ||
                isSavingExit ||
                !applicationData?.applicant_id
              }
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {APPLICANT_CONTENT.application.messages.saving}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {APPLICANT_CONTENT.application.buttons.saveContinue}
                </>
              )}
            </button>
          )}
          {isReadOnly && (
            <button
              data-testid="signature-next-button"
              onClick={() => {
                const paymentPath = applicantId
                  ? getTabPathWithApplicantId("payment", applicantId)
                  : "/applicant/application/payment";
                if (applicantId) {
                  navigate(paymentPath, getTabNavigationOptions("payment"));
                } else {
                  navigate(paymentPath);
                }
              }}
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              {APPLICANT_CONTENT.applyNow.step3.nextButton}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="signature-save-exit-button"
            onClick={() => {
              if (isReadOnly) {
                navigate("/applicant/dashboard", { replace: true });
              } else {
                handleSaveAndContinue(true);
              }
            }}
            disabled={
              !isReadOnly &&
              (isSavingExit ||
                isSaving ||
                !allDocumentsChecked ||
                !applicationData?.applicant_id)
            }
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingExit ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : isReadOnly ? (
              APPLICANT_CONTENT.application.buttons.exit
            ) : (
              APPLICANT_CONTENT.application.buttons.saveExit
            )}
          </button>
        </div>
      </div>

      {/* Validation Modal */}
      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showValidationModal}
        title={APPLICANT_CONTENT.errors.missingFields}
        message={validationModalMessage}
        showValidationIcon={validationShowIcon}
        onClose={() => setShowValidationModal(false)}
        overlayDataTestId={
          validationShowIcon ? undefined : "signature-validation-error-modal"
        }
        closeButtonDataTestId={
          validationShowIcon
            ? undefined
            : "signature-validation-error-modal-close-button"
        }
        confirmButtonDataTestId={
          validationShowIcon
            ? undefined
            : "signature-validation-error-modal-ok-button"
        }
      />
      </div>
    </div>
  );
}
